import * as React from 'react'
import { useCallback } from 'react'
import { StaticImage } from 'gatsby-plugin-image'
import { ProductImage } from '../CategoryImage'
import InputField from '../form/InputField'
import FormWrapper from '../form/FormWrapper'
import { useDispatch } from 'react-redux'
import USD from '../../helpers/NumberHelper'
import finalPrice from '../../helpers/product/finalPrice'
import { hidePopup, sendOffer } from '../../state/popupForms'
import useFormState from '../form/hooks/useFormState'
import PropTypes from 'prop-types'
import { TrackUser, TrackEvent, PersonalInfo } from '../../helpers/TrackingHelper'
import getUTMCodes from '../form/helpers/getUTMCodes'
import isEmail from '../form/validators/isEmail'
import isPhone from '../form/validators/isPhone'
import ProductTitle from '../products/ProductTitle'
import { IFrame } from '../homepage/YouTube'
import GoogleAds from '../../../utils/GoogleAds'
import { currentAgent } from '../../../config/make-an-offer'

const MakeAnOffer = ({ product, category }) => {
  const [form, setForm, updateForm] = useFormState({
    name: '',
    phone: '',
    email: '',
    offer: '',
    message: '',
    consent: true,
  })
  const [offerSent, setOfferSent] = React.useState(true)
  const [offerArgs, setOfferArgs] = React.useState({})

  React.useEffect(() => {
    if (product.sku === offerArgs.sku || !offerSent) return
    setOfferSent(false)
  }, [product, offerArgs, offerSent])
  const toUrlEncoded = obj =>
    Object.entries(obj)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  /*
   {
     name: 'Full Name',
     phone: 'Phone Number (unformatted)',
     email: 'Email Address',
     offer: 'Offer Amount',
     message: 'Custom Message (optional)
     product: {
        name: 'Product Name',
        sku: 'SKU Number',
        price: 'Original Price',
        image: 'URL to product image',
        url: 'URL to product page',
      }
   }
   */
  const dispatch = useDispatch()
  const onSubmit = useCallback(
    async e => {
      try {
        const { form: formInfo } = e
        TrackUser(formInfo.email, PersonalInfo(formInfo))
        TrackEvent(
          'make_an_offer',
          {
            value: formInfo.offer,
            product: product,
            make_an_offer_amount: formInfo.offer,
            currency: 'USD',
          },
          {
            klaviyoEventName: 'Make an Offer',
          }
        )
        TrackEvent(
          'form_submission',
          {
            category: 'Submission',
            label: 'Make an Offer',
            form: formInfo,
            product: product,
            value: formInfo.offer,
            currency: 'USD',
          },
          {
            klaviyoEventName: 'Form Submission',
            googleAdsConversion: GoogleAds.Conversions.make_an_offer,
          }
        )
        const query = getUTMCodes()
        const liveChatArgs = {
          sku: product.sku,
          name: product.title,
          price: finalPrice(product),
          image: `media/catalog/product/${product.sku.toLowerCase()}.jpg`,
          make_an_offer_amount: formInfo.offer,
          full_name: formInfo.name,
          e_mail_address: formInfo.email,
          mobile_phone: formInfo.phone,
          message: formInfo.message,
        }
        setOfferArgs(liveChatArgs)

        let info = await fetch('/api/make-an-offer' + query, {
          method: 'POST',
          body: JSON.stringify({
            ...formInfo,
            product: {
              sku: product.sku,
            },
            _qs: window?.qs,
            _initial_page: window?._initial_page,
          }),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })
        if (info.status !== 200) {
          alert('There was an error submitting your form. Please try again later.')
          return
        }
        dispatch(sendOffer(formInfo))
      } catch (err) {
        // console.log(err)
        alert('There was an error submitting your form. Please try again later.')
      }
    },
    [dispatch, product.sku]
  )
  const closeModal = useCallback(() => {
    dispatch(hidePopup())
  }, [dispatch])
  const afterSubmit = useCallback(
    () => {
      // console.log('Offer sent', success)
      // ** This is the function that will be called after the form is submitted
      setOfferSent(true)
      // ** Instad of closing the modal and opening the chat, we will just open the chat as IFrame
      // Old way: closeModal()
      // Old way: dispatch(openChat(success))
    },
    [dispatch, closeModal, setOfferSent]
  )
  // Function to handle key presses
  const handleKeyPress = e => {
    switch (e.key) {
      case 'Escape':
        dispatch(hidePopup())
        break
      default:
        break
    }
  }
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])
  const SalesReps = {
    Rich: {
      image: (
        <StaticImage
          src="../../images/rich-make-an-offer.jpeg"
          alt="Make Rich an Offer"
          className=" mx-auto aspect-square max-w-16 md:max-w-[150px] rounded-full border border-gray-200"
          imgStyle={{ objectFit: 'cover' }}
        />
      ),
      hello: (
        <>
          Hello! This is Rich, the right hand of Mr. Gray and a specialist with 20+ years of
          experience. I'll be happy to consider your offer and work with you personally. I am the
          decision maker at Gray &amp; Sons!
        </>
      ),
    },
    Viktoria: {
      image: (
        <StaticImage
          src="../../images/viktoria-livechat.jpeg"
          alt="Make Viktoria an Offer"
          className=" mx-auto aspect-square max-w-16 md:max-w-[150px] rounded-full border border-gray-200"
          imgStyle={{ objectFit: 'cover' }}
        />
      ),
      hello: (
        <>
          Hello! This is Viktoria, a specialist with 20+ years of experience. I'll be happy to
          consider your offer and work with you personally. I am the decision maker at Gray &amp;
          Sons!
        </>
      ),
    },
  }

  const onChangeHandler = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.checked }))
  }

  // *** NOTE: Make an offer: Temporary redirection to Viktoria (status: disabled)
  const salesRep = SalesReps[currentAgent.name] || SalesReps.Rich
  return (
    <div className="fade-in-now fixed top-0 left-0 flex h-full w-full overflow-y-auto items-center justify-center lg:h-screen z-popup bg-white md:bg-transparent backdrop-blur-sm">
      <div className="fade-in-later fixed inset-0 p-5 shorter768:rounded-none shorter768:h-screen shorter768:max-h-[90vh] shorter768:w-screen shorter768:max-w-[100vw] lg:relative z-20 w-full md:max-w-[800px] bg-white shadow-lg md:rounded-lg md:max-h-[85vh] m-auto overflow-auto md:pt-5 ">
        <button
          onClick={closeModal}
          className="absolute top-5 md:top-5 right-5 h-8 w-8 rounded-full border-2 border-gray-300 text-lg font-black text-gray-300 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-400"
        >
          &#10005;
        </button>
        <h2 className="pb-2 lg:pb-5 text-center text-3xl font-black text-gray-500">
          MAKE AN OFFER
        </h2>

        {!offerSent && (
          <div className="fade flex shorter768:flex-col flex-row gap-3 justify-center items-center mx-auto max-w-max md:max-w-[800px]">
            <p className="w-max md:hidden lg:leading-relaxed text-white bg-blue-500 max-h-max px-3 py-1 rounded-lg border-t border-t-cyan-400 border-b-2 border-b-blue-800">
              I am a decision maker.
              <br />
              Ready to make a deal Now!
            </p>
            {salesRep.image}

            <p className="hidden md:block mx-auto w-full text-center text-sm leading-relaxed text-gray-600  md:text-base">
              {salesRep.hello}
            </p>
          </div>
        )}
        {offerSent ? (
          <IFrame
            src={
              'https://secure.livechatinc.com/licence/11836182/v2/open_chat.cgi?group=1&unique_groups=1&params=' +
              encodeURIComponent(toUrlEncoded(offerArgs))
            }
            className="shorter768:h-[calc(100vh-6em)] md:h-[calc(85vh-6em)] w-full "
          />
        ) : (
          <FormWrapper
            form={form}
            name={'Make an Offer'}
            onSubmit={onSubmit}
            action={afterSubmit}
            className="grid md:grid-cols-2 m-auto max-w-[800px]"
            saveOnSubmit={false}
            conversionEventName="make_an_offer"
          >
            <div className=" md:col-span-1 md:row-span-1 w-full px-4 flex flex-col h-max ">
              <ProductImage
                image={product.image}
                product={product}
                loading={'eager'}
                className={'max-h-max'}
                imgStyle={{ objectFit: 'contain' }}
              />
              <div className="line-clamp-3 h-20">
                <ProductTitle product={product} category={category} showRef={false} />
              </div>
              {/* This is the price for the mobile top bar component */}
              <p className={' w-full mx-auto text-gray-700 text-xl text-left font-bold '}>
                {USD(finalPrice(product))}
              </p>
            </div>
            <div className="col-span-2 row-span-2 my-auto md:col-span-1 md:row-span-1">
              <InputField
                errorMessage=""
                name={'offer'}
                label={'Offer'}
                form={form}
                onChange={updateForm}
                required={true}
                inputClassName="text-2xl font-black text-red-600"
              />
              <InputField
                errorMessage=""
                name={'name'}
                label={'Name'}
                form={form}
                onChange={updateForm}
                required={true}
              />
              <InputField
                errorMessage=""
                name={'phone'}
                label={'Telephone'}
                form={form}
                validator={isPhone}
                onChange={updateForm}
                required={true}
              />
              <InputField
                errorMessage=""
                name={'email'}
                label={'E-Mail'}
                form={form}
                validator={isEmail}
                onChange={updateForm}
                required={true}
              />
              <InputField
                errorMessage=""
                name={'message'}
                type="textarea"
                rows={4}
                label={'Message'}
                form={form}
                onChange={updateForm}
              />
              <label className="text-xs text-gray-500 flex flex-row gap-3 items-start my-2">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form['consent']}
                  onChange={onChangeHandler}
                />
                I agree to receive updates and follow-up messages about my inquiries via text and
                email.
              </label>
              <button className="mt-2 rounded-md bg-red-600 px-8 py-2 font-bold text-white hover:bg-red-800 border-2 border-red-600">
                SEND OFFER
              </button>
              <button
                onClick={closeModal}
                className="ml-2 mt-2 rounded-md border-2 border-gray-600 px-8 py-2 font-bold text-gray-600"
              >
                CANCEL
              </button>
            </div>
          </FormWrapper>
        )}
      </div>
      <button
        onClick={closeModal}
        className="static hidden md:fixed -top-11 z-10 md:flex h-[calc(100vh+10%)] w-screen items-center justify-center bg-blend-difference bg-slate-500 bg-opacity-40 cursor-alias"
      />
    </div>
  )
}
MakeAnOffer.propTypes = {
  product: PropTypes.object.isRequired,
  category: PropTypes.object.isRequired,
}

export default MakeAnOffer
